import {
  buildReadmeReferralMetadata,
  readReadmeReferralAttributionFromCookieString,
} from "@/lib/marketing/readme-referral-attribution";
import { getPostHogClient, POSTHOG_READY_EVENT } from "@/lib/posthog/client";
import { PRODUCT_EVENT_NAMES } from "@/lib/posthog/product-event-names";

const ALLOWED_PROPERTY_NAMES = new Set([
  "path",
  "source_path",
  "location",
  "target",
  "result",
  "reason",
  "surface",
  "method",
]);

type ProductEventProperties = Record<string, unknown>;
type SafeProductEventProperties = Record<string, string | number | boolean>;

function isProductEventName(eventName: string) {
  return PRODUCT_EVENT_NAMES.has(eventName);
}

function readCurrentPath() {
  if (typeof window === "undefined") {
    return null;
  }

  return window.location.pathname;
}

function sanitizeCode(value: string, maxLength = 96) {
  const normalized = value.trim().toLowerCase().replace(/\s+/g, "_");
  const safe = normalized.replace(/[^a-z0-9_:/.-]/g, "_");
  return safe.slice(0, maxLength) || null;
}

function sanitizePath(value: string) {
  const trimmed = value.trim();
  if (!trimmed || !trimmed.startsWith("/") || trimmed.startsWith("//") || trimmed.includes("\\")) {
    return null;
  }
  return trimmed.slice(0, 256);
}

function sanitizeProperty(name: string, value: unknown) {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  if (name === "path" || name === "source_path") {
    return sanitizePath(value);
  }

  return sanitizeCode(value);
}

function sanitizeProperties(properties: ProductEventProperties) {
  const safeProperties: SafeProductEventProperties = {};
  const mergedProperties = {
    path: readCurrentPath(),
    ...properties,
  };

  for (const [name, value] of Object.entries(mergedProperties)) {
    if (!ALLOWED_PROPERTY_NAMES.has(name)) {
      continue;
    }

    const safeValue = sanitizeProperty(name, value);
    if (safeValue !== null) {
      safeProperties[name] = safeValue;
    }
  }

  return safeProperties;
}

export function getProductEventAttribution(): SafeProductEventProperties {
  if (typeof document === "undefined") {
    return {};
  }

  const attribution = readReadmeReferralAttributionFromCookieString(document.cookie);
  if (!attribution) {
    return {};
  }

  return buildReadmeReferralMetadata(attribution);
}

export function captureProductEvent(
  eventName: string,
  properties: ProductEventProperties = {},
) {
  if (!isProductEventName(eventName)) {
    return false;
  }

  const client = getPostHogClient();
  if (!client) {
    return false;
  }

  try {
    client.capture(eventName, {
      ...sanitizeProperties(properties),
      ...getProductEventAttribution(),
    });
    return true;
  } catch {
    return false;
  }
}

export function captureProductEventWhenReady(
  eventName: string,
  properties: ProductEventProperties = {},
) {
  if (captureProductEvent(eventName, properties)) {
    return true;
  }

  if (!isProductEventName(eventName) || typeof window === "undefined") {
    return false;
  }

  let timeoutId: number | null = null;
  const retry = () => {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
    captureProductEvent(eventName, properties);
  };

  window.addEventListener(
    POSTHOG_READY_EVENT,
    retry,
    { once: true },
  );
  timeoutId = window.setTimeout(() => {
    window.removeEventListener(POSTHOG_READY_EVENT, retry);
  }, 10_000);

  return false;
}
